import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../../shared/lib/hooks/use-auth';

interface PermissionGuardProps {
  permission: string | string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showFallback?: boolean;
  redirectTo?: string;
  requireAll?: boolean; // For multiple permissions, require all or any
}

/**
 * Component that conditionally renders children based on user permissions
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  children,
  fallback = null,
  showFallback = true,
  redirectTo,
  requireAll = false,
}) => {
  const { hasPermission, isSuperuser } = usePermissions();

  // Superuser has access to everything
  if (isSuperuser) {
    return <>{children}</>;
  }

  const permissions = Array.isArray(permission) ? permission : [permission];
  
  const hasAccess = requireAll
    ? permissions.every(p => hasPermission(p))
    : permissions.some(p => hasPermission(p));

  if (!hasAccess) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return showFallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
};

/**
 * Higher-order component for permission-based rendering
 */
export const withPermission = <P extends object>(
  Component: React.ComponentType<P>,
  permission: string | string[],
  options?: {
    fallback?: React.ReactNode;
    redirectTo?: string;
    requireAll?: boolean;
  }
) => {
  const WrappedComponent = (props: P) => (
    <PermissionGuard 
      permission={permission} 
      fallback={options?.fallback}
      redirectTo={options?.redirectTo}
      requireAll={options?.requireAll}
    >
      <Component {...props} />
    </PermissionGuard>
  );

  WrappedComponent.displayName = `withPermission(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

/**
 * Hook for conditional rendering based on permissions
 */
export const usePermissionGuard = (permission: string | string[], requireAll = false) => {
  const { hasPermission, isSuperuser } = usePermissions();

  if (isSuperuser) {
    return true;
  }

  const permissions = Array.isArray(permission) ? permission : [permission];
  
  return requireAll
    ? permissions.every(p => hasPermission(p))
    : permissions.some(p => hasPermission(p));
};